import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { Button, Form, type FormProps, Input, Modal, Switch } from 'antd';
import { updateCategory } from '@/services/api/categoryService';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { Category } from '@/types/categories/category';

type FieldType = {
  name?: string;
  enabled?: boolean;
};

interface ModalEditCategoryProps {
  category: Category | null;
  open: boolean;
  onOk: () => void;
  onCancel: () => void;
  onLoadData: () => void;
}

const ModalEditCategory: FC<ModalEditCategoryProps> = ({
  category,
  open,
  onOk,
  onCancel,
  onLoadData,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (open && category) {
      form.setFieldsValue({
        name: category.name,
        enabled: category.enabled ?? true,
      });
    }
  }, [category, open, form]);

  const onFinish: FormProps<FieldType>['onFinish'] = async values => {
    if (!category) return;

    setIsLoading(true);
    const payload = {
      name: values.name,
      enabled: values.enabled,
    };

    const { success, error } = await updateCategory(category.id, payload);
    if (!success) {
      message.error(error || t('home.categories.modals.edit.error'));
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    onLoadData();
    onCancel();
  };

  return (
    <Modal
      title={t('home.categories.modals.edit.title')}
      open={open}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {t('home.categories.modals.edit.cancel')}
        </Button>,
        <Button
          key="update"
          type="primary"
          onClick={() => form.submit()}
          loading={isLoading}
        >
          {t('home.categories.modals.edit.confirm')}
        </Button>,
      ]}
      onCancel={onCancel}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
      >
        <Form.Item
          name="name"
          label={t('home.categories.modals.edit.nameLabel')}
          rules={[
            {
              required: true,
              message: t('home.categories.modals.edit.validationRequired'),
            },
            {
              min: 1,
              max: 255,
              message: t('home.categories.modals.edit.validationLength'),
            },
          ]}
        >
          <Input
            placeholder={t('home.categories.modals.edit.namePlaceholder')}
          />
        </Form.Item>

        <Form.Item name="enabled" valuePropName="checked">
          <Switch
            checkedChildren={t('home.categories.modals.edit.enabled')}
            unCheckedChildren={t('home.categories.modals.edit.disabled')}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ModalEditCategory;
